/**
 * navigator.onLine only reports whether a network interface is up — an iOS
 * VPN profile (or captive portal) keeps it true with no real connectivity,
 * and supabase-js has no default fetch timeout, so "online" requests can
 * hang forever and the offline fallbacks never fire. Every online-gated call
 * with an offline fallback must be raced against a timeout instead.
 */

export class RequestTimeoutError extends Error {
  constructor(label: string, ms: number) {
    super(`${label} timed out after ${ms}ms`)
    this.name = 'RequestTimeoutError'
  }
}

/** Interactive paths: fail over to the offline store quickly */
export const ONLINE_REQUEST_TIMEOUT_MS = 10_000

/** Background sync ops: more headroom for photo uploads on slow links */
export const SYNC_OP_TIMEOUT_MS = 30_000

export const withTimeout = async <T>(
  promise: PromiseLike<T>,
  ms: number,
  label = 'request'
): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      Promise.resolve(promise),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new RequestTimeoutError(label, ms)), ms)
      }),
    ])
  } finally {
    clearTimeout(timer)
  }
}
